<template>
    <v-card class="revenue-forecast elevation-2">
        <v-card-title class="primary--text pb-2">
            <v-icon left color="primary">mdi-table-large</v-icon>
            Predpokladané mesačné tržby
        </v-card-title>
        <v-card-subtitle class="pt-0">
            Kliknite na projekt v tabuľke pre detail popisu.
        </v-card-subtitle>

        <v-card-text>
            <v-row class="mb-4 align-center top-controls">
                <v-col cols="12" md="4">
                    <v-text-field v-model.number="pricePerMonth" label="Cena/mesiac" type="number" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                    <div class="mb-1 text-caption font-weight-medium">Koeficient platby z tržieb</div>
                    <div class="d-flex align-center slider-row slider-control">
                        <v-slider v-model="payoutPercent" min="50" max="70" step="1" thumb-label class="my-0" />
                        <v-chip small color="success" outlined class="percent-chip">
                            {{ payoutPercent }} %
                        </v-chip>
                    </div>
                </v-col>
            </v-row>

            <v-card outlined class="mb-4">
                <v-card-title class="text-subtitle-1 pb-0">
                    Vstupy podľa rokov
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="4" v-for="year in [1, 2, 3]" :key="year">
                            <div class="text-subtitle-2 mb-2 d-flex align-center">
                                <v-icon small left color="primary">mdi-calendar</v-icon>
                                {{ year }}. rok
                            </div>
                            <v-text-field v-model.number="slovakiaUsers[year]" label="Slovensko" type="number" outlined
                                dense />
                            <v-text-field v-model.number="globalUsers[year]" label="Globál" type="number" outlined
                                dense />
                            <div class="mb-1 text-caption font-weight-medium">Naplnenie plánov</div>
                            <div class="d-flex align-center slider-row slider-control">
                                <v-slider v-model.number="fillFactors[year]" min="0" max="1000" step="1" thumb-label
                                    class="my-0" />
                                <v-chip small color="primary" outlined class="percent-chip">
                                    {{ fillFactors[year] }} %
                                </v-chip>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-card-title class="text-subtitle-1 pb-0">
                    Projekty a výpočty
                </v-card-title>
                <v-card-text class="pa-0">
                    <div class="table-wrap">
                        <v-simple-table dense class="forecast-table">
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th rowspan="2">Projekt č.</th>
                                        <th rowspan="2">Názov projektu</th>
                                        <th rowspan="2">Trh</th>
                                        <th colspan="3" class="text-center">Celkové tržby</th>
                                        <th colspan="3" class="text-center">Suma na vrátenie investorom</th>
                                    </tr>
                                    <tr>
                                        <th>1. rok</th>
                                        <th>2. rok</th>
                                        <th>3. rok</th>
                                        <th>1. rok</th>
                                        <th>2. rok</th>
                                        <th>3. rok</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in projectRows" :key="row.id" class="project-row"
                                        @click="openProjectDetail(row)">
                                        <td>{{ row.order }}</td>
                                        <td class="font-weight-medium">
                                            <div class="d-flex align-center justify-space-between">
                                                <div class="d-flex align-center">
                                                    <v-icon left small color="primary">{{ getProjectIcon(row)
                                                        }}</v-icon>
                                                    <span>{{ row.name }}</span>
                                                </div>
                                                <v-icon x-small color="grey">mdi-chevron-right</v-icon>
                                            </div>
                                        </td>
                                        <td>
                                            <v-chip x-small :color="isGlobalProject(row) ? 'info' : 'primary'" outlined>
                                                {{ isGlobalProject(row) ? 'Globál' : 'Slovensko' }}
                                            </v-chip>
                                        </td>
                                        <td class="text-right">{{ formatCurrency(row.revenue[1]) }}</td>
                                        <td class="text-right">{{ formatCurrency(row.revenue[2]) }}</td>
                                        <td class="text-right">{{ formatCurrency(row.revenue[3]) }}</td>
                                        <td class="text-right success--text">{{ formatCurrency(row.payout[1]) }}</td>
                                        <td class="text-right success--text">{{ formatCurrency(row.payout[2]) }}</td>
                                        <td class="text-right success--text">{{ formatCurrency(row.payout[3]) }}</td>
                                    </tr>
                                    <tr class="font-weight-bold totals-row">
                                        <td></td>
                                        <td>Spolu</td>
                                        <td>-</td>
                                        <td class="text-right">{{ formatCurrency(totalsByYear[1]) }}</td>
                                        <td class="text-right">{{ formatCurrency(totalsByYear[2]) }}</td>
                                        <td class="text-right">{{ formatCurrency(totalsByYear[3]) }}</td>
                                        <td class="text-right">{{ formatCurrency(payoutTotalsByYear[1]) }}</td>
                                        <td class="text-right">{{ formatCurrency(payoutTotalsByYear[2]) }}</td>
                                        <td class="text-right">{{ formatCurrency(payoutTotalsByYear[3]) }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </div>
                </v-card-text>
            </v-card>

            <v-row class="mt-4">
                <v-col cols="12" md="4" v-for="year in [1, 2, 3]" :key="`summary-${year}`">
                    <v-card outlined class="summary-card">
                        <v-card-text class="text-center">
                            <div class="text-caption">{{ year }}. rok</div>
                            <div class="text-body-2">Tržby: <strong class="primary--text">{{
                                    formatCurrency(totalsByYear[year])
                                    }}</strong></div>
                            <div class="text-body-2">Investori: <strong class="success--text">{{
                                    formatCurrency(payoutTotalsByYear[year]) }}</strong></div>
                            <div class="text-body-2">Spoločnosť: <strong class="primary--text">{{
                                    formatCurrency(companyTotalsByYear[year]) }}</strong></div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-dialog v-model="projectDialog" max-width="760px">
                <v-card v-if="selectedProject">
                    <v-card-title class="primary white--text">
                        <v-icon left color="white">{{ getProjectIcon(selectedProject) }}</v-icon>
                        <div class="d-flex flex-column">
                            <span>{{ selectedProject.name }}</span>
                            <span class="text-caption white--text">
                                Projekt č. {{ selectedProject.order }}
                            </span>
                        </div>
                    </v-card-title>
                    <v-card-text class="pa-6">
                        <div class="mb-3">
                            <v-chip v-if="selectedProject.category" small outlined color="primary">
                                {{ formatCategoryLabel(selectedProject.category) }}
                            </v-chip>
                        </div>
                        <div class="text-body-1 description-scroll">
                            {{ selectedProject.description || 'Popis projektu nie je dostupný.' }}
                        </div>
                    </v-card-text>
                    <v-card-actions class="px-6 pb-4">
                        <v-spacer />
                        <v-btn text color="primary" @click="projectDialog = false">Zavrieť</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-text>
    </v-card>
</template>

<script>
import { FALLBACK_PROJECTS, FORECAST_DEFAULTS, DEFAULT_ANNUAL_REVENUE } from '@/config/investment';

export default {
    name: 'RevenueForecastTable',

    props: {
        projects: {
            type: Array,
            default: () => []
        },
        annualRevenue: {
            type: Number,
            default: DEFAULT_ANNUAL_REVENUE
        },
        realRevenues: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            pricePerMonth: FORECAST_DEFAULTS.pricePerMonth,

            payoutPercent: FORECAST_DEFAULTS.payoutPercent,

            slovakiaUsers: { ...FORECAST_DEFAULTS.slovakiaUsers },

            globalUsers: { ...FORECAST_DEFAULTS.globalUsers },

            fillFactors: { ...FORECAST_DEFAULTS.fillFactors },

            projectDialog: false,
            selectedProject: null
        };
    },

    computed: {
        normalizedProjects() {
            if (Array.isArray(this.projects) && this.projects.length > 0) {
                return this.projects
                    .slice()
                    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
                    .map((p, index) => ({
                        id: p.id || `project-${index}`,
                        order: Number.isFinite(Number(p.order)) ? Number(p.order) : (index + 1),
                        name: p.name || `Projekt ${index + 1}`,
                        description: p.description || '',
                        is_global: this.normalizeBoolean(p.is_global),
                        category: p.category || '',
                        source: p
                    }));
            }

            return FALLBACK_PROJECTS.map((p, index) => ({
                id: `fallback-${index}`,
                order: p.order,
                name: p.name,
                description: '',
                is_global: false,
                category: '',
                source: null
            }));
        },

        payoutCoefficient() {
            return this.normalizeNumber(this.payoutPercent) / 100;
        },

        projectRows() {
            return this.normalizedProjects.map((project) => {
                const isGlobalRow = this.isGlobalProject(project);

                const revenue = {
                    1: this.getBaseUsers(isGlobalRow, 1) * this.normalizeNumber(this.pricePerMonth) * 12,
                    2: this.getBaseUsers(isGlobalRow, 2) * this.normalizeNumber(this.pricePerMonth) * 12,
                    3: this.getBaseUsers(isGlobalRow, 3) * this.normalizeNumber(this.pricePerMonth) * 12
                };

                const payout = {
                    1: revenue[1] * this.payoutCoefficient,
                    2: revenue[2] * this.payoutCoefficient,
                    3: revenue[3] * this.payoutCoefficient
                };

                return {
                    ...project,
                    revenue,
                    payout
                };
            });
        },

        rawTotalsByYear() {
            return {
                1: this.projectRows.reduce((sum, row) => sum + row.revenue[1], 0),
                2: this.projectRows.reduce((sum, row) => sum + row.revenue[2], 0),
                3: this.projectRows.reduce((sum, row) => sum + row.revenue[3], 0)
            };
        },

        rawPayoutTotalsByYear() {
            return {
                1: this.projectRows.reduce((sum, row) => sum + row.payout[1], 0),
                2: this.projectRows.reduce((sum, row) => sum + row.payout[2], 0),
                3: this.projectRows.reduce((sum, row) => sum + row.payout[3], 0)
            };
        },

        totalsByYear() {
            return {
                1: this.rawTotalsByYear[1] * (this.normalizeNumber(this.fillFactors[1]) / 100),
                2: this.rawTotalsByYear[2] * (this.normalizeNumber(this.fillFactors[2]) / 100),
                3: this.rawTotalsByYear[3] * (this.normalizeNumber(this.fillFactors[3]) / 100)
            };
        },

        payoutTotalsByYear() {
            return {
                1: this.rawPayoutTotalsByYear[1] * (this.normalizeNumber(this.fillFactors[1]) / 100),
                2: this.rawPayoutTotalsByYear[2] * (this.normalizeNumber(this.fillFactors[2]) / 100),
                3: this.rawPayoutTotalsByYear[3] * (this.normalizeNumber(this.fillFactors[3]) / 100)
            };
        },

        companyTotalsByYear() {
            return {
                1: this.totalsByYear[1] - this.payoutTotalsByYear[1],
                2: this.totalsByYear[2] - this.payoutTotalsByYear[2],
                3: this.totalsByYear[3] - this.payoutTotalsByYear[3]
            };
        },

        emittedRevenue() {
            return this.totalsByYear[1] || 0;
        }
    },

    watch: {
        emittedRevenue: {
            immediate: true,
            handler(value) {
                this.$emit('revenue-change', value);
            }
        }
    },

    methods: {
        isGlobalProject(project) {
            const directIsGlobal = project && this.normalizeBoolean(project.is_global);
            const sourceIsGlobal = project && project.source && this.normalizeBoolean(project.source.is_global);
            return directIsGlobal || sourceIsGlobal;
        },

        getBaseUsers(isGlobalRow, year) {
            if (isGlobalRow) {
                return this.normalizeNumber(this.globalUsers[year]);
            }
            return this.normalizeNumber(this.slovakiaUsers[year]);
        },

        normalizeNumber(value) {
            const num = Number(value);
            return Number.isFinite(num) ? num : 0;
        },

        normalizeBoolean(value) {
            return value === true || value === 1 || value === '1' || value === 'true';
        },

        openProjectDetail(project) {
            this.selectedProject = project;
            this.projectDialog = true;
        },

        getProjectIcon(project) {
            const rawCategory = String(project && project.category ? project.category : '').trim();
            if (rawCategory.startsWith('mdi-')) {
                return rawCategory;
            }

            const category = rawCategory.toLowerCase();
            if (category.includes('transport') || category.includes('taxi') || category.includes('kur')) return 'mdi-taxi';
            if (category.includes('security') || category.includes('guard')) return 'mdi-shield-check';
            if (category.includes('retail') || category.includes('predaj')) return 'mdi-store';
            if (category.includes('advert') || category.includes('media') || category.includes('ads') || category.includes('rekl')) return 'mdi-bullhorn';
            if (category.includes('global')) return 'mdi-earth';
            return 'mdi-information-outline';
        },

        formatCategoryLabel(value) {
            return String(value || '')
                .replace(/^mdi-[a-z0-9-]+$/i, 'Kategória projektu')
                .replace(/_/g, ' ')
                .trim();
        },

        formatCurrency(amount) {
            const num = this.normalizeNumber(amount);
            return new Intl.NumberFormat('sk-SK', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }).format(num);
        }
    }
};
</script>

<style scoped>
.revenue-forecast {
    margin-bottom: 24px;
}

.table-wrap {
    overflow-x: auto;
}

.slider-row {
    min-height: 40px;
}

.slider-control {
    gap: 12px;
}

.slider-control :deep(.v-input) {
    margin-top: 0 !important;
    padding-top: 0 !important;
    flex: 0 1 320px;
    max-width: 320px;
}

.percent-chip {
    min-width: 72px;
    justify-content: center;
}

@media (max-width: 960px) {
    .slider-control {
        justify-content: flex-start;
    }

    .slider-control :deep(.v-input) {
        flex: 1 1 auto;
        max-width: none;
    }
}

.forecast-table th,
.forecast-table td {
    white-space: nowrap;
    padding: 10px 12px !important;
}

.forecast-table thead th {
    background: #f3f7fb;
    color: #1f3b5b;
    font-weight: 700;
    border-bottom: 1px solid #dce6f1;
}

.project-row {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.project-row:hover {
    background-color: #f7fbff;
}

.project-row:hover .mdi-chevron-right {
    color: #3f51b5 !important;
}

.forecast-table tbody tr:nth-child(odd):not(.totals-row) {
    background-color: #fcfdff;
}

.totals-row {
    background: #e8f3ff !important;
}

.summary-card {
    border-color: #dce6f1 !important;
}

.description-scroll {
    max-height: 320px;
    overflow-y: auto;
    line-height: 1.6;
    white-space: pre-line;
}
</style>
